import { Variants } from "../../configs/configs.types"
import { IRanger } from "./Ranger.types"
import { useEffect, useRef, useState } from "react"
import { formatCurrency, formatNumber } from "../../utils/formatNumber"
import "./ranger.sass"

const variant = process.env.REACT_APP_VARIANT ?? Variants.v0

function getThumbWidth(isMobile: boolean) {
  switch (variant) {
    case "v0":
    case "v5":
      return isMobile ? 140 : 165
    case "v1":
    case "v2":
      return isMobile ? 124 : 150
    case "v3":
      return 145
    default:
      return 165
  }
}

function snapRangeValue(value: number, min: number, max: number, step: number) {
  if (!Number.isFinite(value)) return min
  const clamped = Math.min(max, Math.max(min, value))
  if (step <= 0) return clamped
  const snapped = Math.round((clamped - min) / step) * step + min
  return Math.min(max, Math.max(min, snapped))
}

function TypedRanger({ props, firstValue, onChange }: IRanger) {
  const [draft, setDraft] = useState<string | null>(null)
  const range = Math.max(props.max - props.min, 1)
  const fillPercent = ((firstValue - props.min) / range) * 100

  function commitValue(raw: string) {
    const parsed = Number(raw.replace(/\D/g, ""))
    onChange(snapRangeValue(parsed, props.min, props.max, props.step))
    setDraft(null)
  }

  function handleInputChange(raw: string) {
    const digits = raw.replace(/\D/g, "").slice(0, 8)
    setDraft(digits ? formatNumber(Number(digits)) : "")
    const parsed = Number(digits)
    if (Number.isFinite(parsed) && parsed >= props.min && parsed <= props.max) {
      onChange(parsed)
    }
  }

  return (
    <div className="ranger ranger--typed">
      <div className="ranger__input-row">
        <label className="ranger__input-label" htmlFor="ranger-amount-input">
          {props.name}
        </label>
        <div className="ranger__input-box">
          <span className="ranger__input-currency" aria-hidden="true">
            {props.currency}
          </span>
          <input
            id="ranger-amount-input"
            className="ranger__input"
            inputMode="numeric"
            value={draft ?? formatNumber(firstValue)}
            onChange={e => handleInputChange(e.target.value)}
            onBlur={e => commitValue(e.target.value)}
            aria-label={props.name}
          />
        </div>
      </div>
      <div className="ranger__slider-wrap">
        <input
          className="ranger__slider"
          type="range"
          min={props.min}
          max={props.max}
          step={props.step}
          value={firstValue}
          onChange={e => {
            setDraft(null)
            onChange(Number(e.target.value))
          }}
          aria-label={props.name}
          style={{
            backgroundImage: `linear-gradient(90deg, #BC16D3 0%, #80158F ${fillPercent}%, #F0E4F3 ${fillPercent}%, #F0E4F3 100%)`
          }}
        />
      </div>
      <div className="ranger__limits">
        <span>{formatCurrency(props.min, props.currency, props.currencyPosition)}</span>
        <span>{formatCurrency(props.max, props.currency, props.currencyPosition)}</span>
      </div>
    </div>
  )
}

function DefaultRanger({ props, firstValue, onChange }: IRanger) {
  const ref = useRef<HTMLInputElement>(null)
  const [positionRange, setPositionRange] = useState(0)
  const stateRef = useRef({
    firstValue,
    min: props.min,
    max: props.max,
    isMobile: typeof window !== "undefined" && window.innerWidth <= 480
  })
  stateRef.current = { firstValue, min: props.min, max: props.max, isMobile: typeof window !== "undefined" && window.innerWidth <= 480 }

  function updatePositionFromWidth(width: number) {
    const { firstValue: v, min, max, isMobile } = stateRef.current
    const percent = ((v - min) / (max - min)) * 100
    const thumbWidth = getThumbWidth(isMobile)
    setPositionRange((percent * (width - thumbWidth)) / 100)
  }

  useEffect(() => {
    const el = ref.current
    if (!el) return
    stateRef.current.isMobile = window.innerWidth <= 480
    updatePositionFromWidth(el.getBoundingClientRect().width)
  }, [firstValue])

  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver(() => {
      stateRef.current.isMobile = window.innerWidth <= 480
      updatePositionFromWidth(el.getBoundingClientRect().width)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    const onWindowResize = () => {
      stateRef.current.isMobile = window.innerWidth <= 480
      const el = ref.current
      if (el) updatePositionFromWidth(el.getBoundingClientRect().width)
    }
    window.addEventListener("resize", onWindowResize)
    return () => window.removeEventListener("resize", onWindowResize)
  }, [])

  function setValueRange(sum: number) {
    onChange(sum)
  }
  function editValueRange(sum: number) {
    let newValue = firstValue + sum
    if (newValue < props.min) {
      onChange(props.min)
      return
    }
    if (newValue > props.max) {
      onChange(props.max)
      return
    }
    sum < 0
      ? onChange(Math.ceil(newValue / props.step) * props.step)
      : onChange(Math.floor(newValue / props.step) * props.step)
  }

  if (variant === "v1") {
    return (
      <div className="ranger">
        <p className="ranger__title">
          {props.currencyPosition === "prefix" ? (
            <span>{formatCurrency(firstValue, props.currency, props.currencyPosition)}</span>
          ) : (
            <>
              <span>{formatNumber(firstValue)}</span>
              <span className="ranger__title-sub">{props.currency}</span>
            </>
          )}
        </p>
        <div className="ranger__container">
          <button
            className="ranger__button"
            type="button"
            onClick={() => editValueRange(-props.step)}
            id="range_minus"
            aria-label="Snížit částku"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6 11H11H13H18C18.5523 11 19 11.4477 19 12C19 12.5523 18.5523 13 18 13H13H11H6C5.44772 13 5 12.5523 5 12C5 11.4477 5.44772 11 6 11Z"
              />
            </svg>
          </button>
          <div className="ranger__progress-bar">
            <div className="ranger__progress-value" style={{ left: `${positionRange}px` }}>
              <div className="ranger__progress-value-item">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.79289 7.61872C3.40237 7.27701 3.40237 6.72299 3.79289 6.38128L8.79289 2.00628C9.18342 1.66457 9.81658 1.66457 10.2071 2.00628C10.5976 2.34799 10.5976 2.90201 10.2071 3.24372L5.91421 7L10.2071 10.7563C10.5976 11.098 10.5976 11.652 10.2071 11.9937C9.81658 12.3354 9.18342 12.3354 8.79289 11.9937L3.79289 7.61872Z"
                  />
                </svg>
              </div>
              <div className="ranger__progress-value-item-curr">
                {formatCurrency(firstValue, props.currency, props.currencyPosition)}
              </div>
              <div className="ranger__progress-value-item">
                <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.2071 7.61872C10.5976 7.27701 10.5976 6.72299 10.2071 6.38128L5.20711 2.00628C4.81658 1.66457 4.18342 1.66457 3.79289 2.00628C3.40237 2.34799 3.40237 2.90201 3.79289 3.24372L8.08579 7L3.79289 10.7563C3.40237 11.098 3.40237 11.652 3.79289 11.9937C4.18342 12.3354 4.81658 12.3354 5.20711 11.9937L10.2071 7.61872Z"
                  />
                </svg>
              </div>
            </div>
            <input
              ref={ref}
              className="ranger__progress"
              type="range"
              min={props.min}
              max={props.max}
              step={props.step}
              value={firstValue}
              onChange={e => setValueRange(Number(e.target.value))}
              aria-label={props.name}
            />
          </div>
          <button
            className="ranger__button"
            type="button"
            onClick={() => editValueRange(+props.step)}
            id="range_plus"
            aria-label="Zvýšit částku"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7 0C7.55228 0 8 0.447715 8 1V6H13C13.5523 6 14 6.44772 14 7C14 7.55228 13.5523 8 13 8H8V13C8 13.5523 7.55228 14 7 14C6.44772 14 6 13.5523 6 13V8H1C0.447715 8 0 7.55228 0 7C0 6.44772 0.447715 6 1 6H6V1C6 0.447715 6.44772 0 7 0Z"
              />
            </svg>
          </button>
        </div>
      </div>
    )
  }

  if (variant === "v2") {
    return (
      <div className="ranger">
        <div className="ranger__container">
          <button
            className="ranger__button"
            type="button"
            onClick={() => editValueRange(-props.step)}
            id="range_minus"
            aria-label="Snížit částku"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6 11H11H13H18C18.5523 11 19 11.4477 19 12C19 12.5523 18.5523 13 18 13H13H11H6C5.44772 13 5 12.5523 5 12C5 11.4477 5.44772 11 6 11Z"
              />
            </svg>
          </button>
          <div className="ranger__progress-bar">
            <div className="ranger__progress-value" style={{ left: `${positionRange}px` }}>
              <div className="ranger__progress-value-item">
                {formatCurrency(firstValue, props.currency, props.currencyPosition)}
              </div>
            </div>
            <input
              ref={ref}
              className="ranger__progress"
              type="range"
              min={props.min}
              max={props.max}
              step={props.step}
              value={firstValue}
              onChange={e => setValueRange(Number(e.target.value))}
              aria-label={props.name}
            />
          </div>
          <button
            className="ranger__button"
            type="button"
            onClick={() => editValueRange(+props.step)}
            id="range_plus"
            aria-label="Zvýšit částku"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7 0C7.55228 0 8 0.447715 8 1V6H13C13.5523 6 14 6.44772 14 7C14 7.55228 13.5523 8 13 8H8V13C8 13.5523 7.55228 14 7 14C6.44772 14 6 13.5523 6 13V8H1C0.447715 8 0 7.55228 0 7C0 6.44772 0.447715 6 1 6H6V1C6 0.447715 6.44772 0 7 0Z"
              />
            </svg>
          </button>
        </div>
      </div>
    )
  }

  if (variant === "v3") {
    return (
      <div className="ranger">
        <div className="ranger__container">
          <button
            className="ranger__button"
            type="button"
            onClick={() => editValueRange(-props.step)}
            id="range_minus"
            aria-label="Snížit částku"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6 11H11H13H18C18.5523 11 19 11.4477 19 12C19 12.5523 18.5523 13 18 13H13H11H6C5.44772 13 5 12.5523 5 12C5 11.4477 5.44772 11 6 11Z"
              />
            </svg>
          </button>
          <div className="ranger__progress-bar">
            <div className="ranger__progress-value" style={{ left: `${positionRange}px` }}>
              <div className="ranger__progress-value-item">
                {formatCurrency(firstValue, props.currency, props.currencyPosition)}
              </div>
            </div>
            <input
              ref={ref}
              className="ranger__progress"
              type="range"
              min={props.min}
              max={props.max}
              step={props.step}
              value={firstValue}
              onChange={e => setValueRange(Number(e.target.value))}
              aria-label={props.name}
            />
          </div>
          <button
            className="ranger__button"
            type="button"
            onClick={() => editValueRange(+props.step)}
            id="range_plus"
            aria-label="Zvýšit částku"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7 0C7.55228 0 8 0.447715 8 1V6H13C13.5523 6 14 6.44772 14 7C14 7.55228 13.5523 8 13 8H8V13C8 13.5523 7.55228 14 7 14C6.44772 14 6 13.5523 6 13V8H1C0.447715 8 0 7.55228 0 7C0 6.44772 0.447715 6 1 6H6V1C6 0.447715 6.44772 0 7 0Z"
              />
            </svg>
          </button>
        </div>
        <div className="ranger__title">
          <span className="ranger__title-sub">
            <p>Vrátit se:</p>
            <p>
              <strong>{formatNumber(firstValue)}</strong>
            </p>
          </span>
          <span className="ranger__title-sub">
            <p>K datu:</p>
            <p>
              <strong>22.11.2024</strong>
            </p>
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="ranger">
      <p className="ranger__title">{props.name}</p>
      <div className="ranger__container">
        <button
          className="ranger__button"
          type="button"
          onClick={() => editValueRange(-props.step)}
          id="range_minus"
          aria-label="Snížit částku"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6 11H11H13H18C18.5523 11 19 11.4477 19 12C19 12.5523 18.5523 13 18 13H13H11H6C5.44772 13 5 12.5523 5 12C5 11.4477 5.44772 11 6 11Z"
            />
          </svg>
        </button>
        <div className="ranger__progress-bar">
          <div className="ranger__progress-value" style={{ left: `${positionRange}px` }}>
            <div className="ranger__progress-value-item white-line__left">
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3.79289 7.61872C3.40237 7.27701 3.40237 6.72299 3.79289 6.38128L8.79289 2.00628C9.18342 1.66457 9.81658 1.66457 10.2071 2.00628C10.5976 2.34799 10.5976 2.90201 10.2071 3.24372L5.91421 7L10.2071 10.7563C10.5976 11.098 10.5976 11.652 10.2071 11.9937C9.81658 12.3354 9.18342 12.3354 8.79289 11.9937L3.79289 7.61872Z"
                />
              </svg>
            </div>
            <div className="ranger__progress-value-item">
              <span>
                {formatCurrency(firstValue, props.currency, props.currencyPosition)}
              </span>
            </div>
            <div className="ranger__progress-value-item white-line__right">
              <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.2071 7.61872C10.5976 7.27701 10.5976 6.72299 10.2071 6.38128L5.20711 2.00628C4.81658 1.66457 4.18342 1.66457 3.79289 2.00628C3.40237 2.34799 3.40237 2.90201 3.79289 3.24372L8.08579 7L3.79289 10.7563C3.40237 11.098 3.40237 11.652 3.79289 11.9937C4.18342 12.3354 4.81658 12.3354 5.20711 11.9937L10.2071 7.61872Z"
                />
              </svg>
            </div>
          </div>
          <input
            ref={ref}
            className="ranger__progress"
            type="range"
            min={props.min}
            max={props.max}
            step={props.step}
            value={firstValue}
            onChange={e => setValueRange(Number(e.target.value))}
            aria-label={props.name}
          />
        </div>
        <button
          className="ranger__button"
          type="button"
          onClick={() => editValueRange(+props.step)}
          id="range_plus"
          aria-label="Zvýšit částku"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M7 0C7.55228 0 8 0.447715 8 1V6H13C13.5523 6 14 6.44772 14 7C14 7.55228 13.5523 8 13 8H8V13C8 13.5523 7.55228 14 7 14C6.44772 14 6 13.5523 6 13V8H1C0.447715 8 0 7.55228 0 7C0 6.44772 0.447715 6 1 6H6V1C6 0.447715 6.44772 0 7 0Z"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}

export function Ranger({ showTypedInput, ...rangerProps }: IRanger) {
  if (showTypedInput) return <TypedRanger {...rangerProps} />
  return <DefaultRanger {...rangerProps} />
}
